'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { HalfModalPortal } from '@/components/layout/modals/HalfModalPortal';
import { Button } from '@/components/ui/button/Button';
import { IconAlertCircle, IconLivenodeLogo, IconLogo } from '@/components/ui/icons';
import { PATHS } from '@/constants/path';
import { cn } from '@/utils/cn';

type TabType = 'register' | 'login';

type AuthHalfModalContentProps = {
  initialTab: TabType;
  onClickStartMigration: () => void;
  onOpenNextModal: () => void;
};

export const AuthHalfModalContent = ({
  initialTab,
  onClickStartMigration,
  onOpenNextModal,
}: AuthHalfModalContentProps) => {
  const t = useTranslations();
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  return (
    <div className='flex w-full flex-col items-center gap-9'>
      <IconLogo width={137} height={26} color='black' />
      <div className='w-full'>
        <div className='border-stroke-subtle flex justify-center border-b'>
          <div className='relative flex gap-4' role='tablist'>
            <button
              type='button'
              id='tab-register'
              role='tab'
              aria-selected={activeTab === 'register'}
              aria-controls='panel-register'
              onClick={() => setActiveTab('register')}
              className={cn(
                'relative px-6 pb-5 transition-colors',
                activeTab === 'register' ? 'text-black' : 'text-foreground-muted',
              )}
            >
              <span className='text-label-16'>{t('common-sign_up')}</span>
              {activeTab === 'register' && (
                <div className='bg-func-primary absolute -bottom-[1px] left-0 h-[2px] w-full' />
              )}
            </button>
            <button
              type='button'
              id='tab-login'
              role='tab'
              aria-selected={activeTab === 'login'}
              aria-controls='panel-login'
              onClick={() => setActiveTab('login')}
              className={cn(
                'relative px-6 pb-5 transition-colors',
                activeTab === 'login' ? 'text-black' : 'text-foreground-muted',
              )}
            >
              <span className='text-label-16'>{t('common-login')}</span>
              {activeTab === 'login' && (
                <div className='bg-func-primary absolute -bottom-[1px] left-0 h-[2px] w-full' />
              )}
            </button>
          </div>
        </div>

        <div className='relative w-full'>
          {activeTab === 'register' && (
            <div
              id='panel-register'
              role='tabpanel'
              aria-labelledby='tab-register'
              className='flex flex-col gap-6 px-5 pt-8'
            >
              <LiveNodeInfo activeTab={activeTab} />
              <div className='px-5'>
                <Button
                  label={t('livenode-id-sign-up_button')}
                  onClick={onOpenNextModal}
                  color='gradient'
                  fullWidth
                />
              </div>
            </div>
          )}

          {activeTab === 'login' && (
            <div
              id='panel-login'
              role='tabpanel'
              aria-labelledby='tab-login'
              className='flex flex-col gap-10 px-5 pt-8'
            >
              <LiveNodeInfo activeTab={activeTab} />
              <div className='flex flex-col gap-6'>
                <div className='flex flex-col gap-4'>
                  <p className='text-body-16'>{t('livenode-id-migration-not-completed_title')}</p>
                  <div className='px-5'>
                    <Button
                      label={t('livenode-id-migration-start_button')}
                      color='gradient'
                      fullWidth
                      onClick={onClickStartMigration}
                    />
                  </div>
                </div>
                <div className='mx-2 border-t border-kl-gray-subtle' />
                <div className='flex flex-col gap-4'>
                  <p className='text-body-16'>{t('livenode-id-migration-completed_title')}</p>
                  <div className='px-5'>
                    <Button
                      label={t('livenode-id-login_button')}
                      onClick={onOpenNextModal}
                      color='gradient'
                      fullWidth
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LiveNodeInfo = ({ activeTab }: { activeTab: 'register' | 'login' }) => {
  const t = useTranslations();

  return (
    <div className='border-stroke-subtle rounded-lg border bg-white p-4'>
      <div className='flex items-start gap-4'>
        <div className='flex size-[48px] shrink-0 items-center justify-center rounded-[2px] p-[4px]'>
          <IconLivenodeLogo className='size-full' />
        </div>
        <div className='flex flex-col gap-3'>
          <p className='text-left text-body-12'>
            {activeTab === 'register'
              ? t('livenode-id-required_description')
              : t('livenode-id-login-integration_description')}
          </p>
          <div className='flex items-center gap-1'>
            <IconAlertCircle width='16' height='16' color='#999999' />
            <a
              href={PATHS.LIVENODE_ID_ABOUT}
              target='_blank'
              className='text-foreground-muted text-body-12 underline'
              rel='noopener noreferrer'
            >
              {t('livenode-id-about_button')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

type AuthHalfModalProps = AuthHalfModalContentProps & {
  isOpen: boolean;
  onClose: () => void;
};

export const AuthHalfModal = ({
  isOpen,
  onClose,
  initialTab,
  onClickStartMigration,
  onOpenNextModal,
}: AuthHalfModalProps) => {
  return (
    <HalfModalPortal isOpen={isOpen} cancelable={true} withSidePadding={false} onClose={onClose}>
      <AuthHalfModalContent
        initialTab={initialTab}
        onClickStartMigration={onClickStartMigration}
        onOpenNextModal={onOpenNextModal}
      />
    </HalfModalPortal>
  );
};
